/**
 * Career paycheck eligibility — the single source of truth.
 *
 * A learner only earns weekly career pay after they've gone through the
 * approved Career Path module and intentionally selected a career. The
 * Finance Center reflects that decision; it never creates it.
 */

import type { CareerState } from "@/hooks/useCareerActivation";

/** Approved career-selection surface. Update here if the module slug/route changes. */
export const CAREER_SELECTION_MODULE_SLUG = "career_path";
export const CAREER_SELECTION_ROUTE = "/career-path";

/** Comp types we can actually pay out weekly. */
const SUPPORTED_COMP_TYPES = new Set([
  "salary",
  "hourly",
  "base_plus_commission",
  "contract",
]);

export type CareerLockReason =
  | "no_profile"
  | "no_career_key"
  | "no_activated_at"
  | "invalid_salary"
  | "unsupported_comp_type";

export interface EligibilityResult {
  eligible: boolean;
  reason: CareerLockReason | null;
}

/**
 * The career row in learner_career_state is only ever inserted by the
 * `apply_career_path_attempt` RPC after the Career Path module is passed,
 * so its mere presence (with valid fields) is proof of intentional selection.
 */
export function isCareerPaycheckEligible(
  profile: CareerState | null | undefined,
): EligibilityResult {
  if (!profile) return { eligible: false, reason: "no_profile" };
  if (!profile.career_key || profile.career_key.trim() === "")
    return { eligible: false, reason: "no_career_key" };
  if (!profile.activated_at) return { eligible: false, reason: "no_activated_at" };
  if (!SUPPORTED_COMP_TYPES.has(profile.comp_type))
    return { eligible: false, reason: "unsupported_comp_type" };
  const salary = Number(profile.annual_income);
  if (!Number.isFinite(salary) || salary <= 0)
    return { eligible: false, reason: "invalid_salary" };
  return { eligible: true, reason: null };
}

/** Friendly copy mapped to lock reasons. */
export function describeLock(reason: CareerLockReason | null): {
  title: string;
  body: string;
  cta: string;
} {
  // Today all reasons funnel learners to the same module — copy is shared.
  return {
    title: "Career Paycheck Locked",
    body:
      "Choose a career in the Career Path module to unlock weekly paychecks. Once your career is set, HeirWise will calculate your estimated weekly pay, taxes, and take-home deposit.",
    cta: "Go to Career Module",
  };
}